import { T } from "../design/tokens";
import { IDIOMA, LOCALE } from "../i18n/idioma";

/* Carátula de episodio, generada en código y determinista: el mismo episodio pinta siempre la
   misma. Sin imágenes ni IA. Un anillo de barras de sonido recorre la duración del episodio y cada
   capítulo es un tramo, tintado naranja → turquesa como el anillo del botón de reproducir. En la
   conversación, las barras alternan dos tonos (dos voces). `forma`: "cuadrada" | "ancha". */

const PROGRAMA = {
  edicion_hablada: { es: "Yamacoa al día", en: "Yamacoa Today" },
  conversacion: { es: "La conversación", en: "The Yamacoa Conversation" },
};
// El nombre del programa va en el idioma del EPISODIO (es un título), la fecha en el de la página.
export const programaDe = (p) => {
  const idi = p.idioma || "es";
  return (PROGRAMA[p.formato] || { es: "Yamacoa · Estudio", en: "Yamacoa · Studio" })[idi];
};

function semilla(id) {
  let h = 2166136261;
  for (const c of String(id)) h = Math.imul(h ^ c.charCodeAt(0), 16777619) >>> 0;
  return () => ((h = (Math.imul(h, 1103515245) + 12345) >>> 0) / 4294967296);
}

function mezcla(a, b, k) {
  const x = (s, i) => parseInt(s.slice(1 + 2 * i, 3 + 2 * i), 16);
  return `rgb(${[0, 1, 2].map((i) => Math.round(x(a, i) + (x(b, i) - x(a, i)) * k)).join(",")})`;
}

export default function Caratula({ p, forma = "cuadrada", style, className }) {
  if (!p) return null;
  const ancha = forma === "ancha";
  const W = ancha ? 320 : 200, H = ancha ? 150 : 200;
  // Cuadrada: el anillo en la esquina inferior derecha, recortado; el texto arriba a la izquierda.
  const cx = ancha ? W - 66 : 150, cy = ancha ? H / 2 : 140;
  const r0 = ancha ? 28 : 34, largo = ancha ? 38 : 34;
  const N = ancha ? 84 : 96;

  const azar = semilla(p.id || p.slug);
  const total = p.duracion || 1;
  const caps = (p.capitulos || []).map((c) => c.inicio / total).filter((f) => f >= 0 && f < 1);
  const nCaps = Math.max(1, caps.length);
  const conversa = p.formato === "conversacion";
  const fase = azar() * 6;

  const barras = [];
  for (let i = 0; i < N; i++) {
    const f = i / N;
    let cap = 0;
    while (cap + 1 < caps.length && f >= caps[cap + 1]) cap++;
    // Hueco de una barra al empezar cada capítulo (salvo el primero): se leen los tramos.
    if (cap > 0 && i > 0 && (i - 1) / N < caps[cap]) continue;
    const v = 0.22 + 0.78 * Math.abs(Math.sin(i / 3.1 + fase)) * (0.45 + 0.55 * azar());
    const a = -Math.PI / 2 + f * 2 * Math.PI;
    const r1 = r0 + 3, r2 = r0 + 3 + v * largo;
    const k = nCaps > 1 ? cap / (nCaps - 1) : 0.5;
    const color = conversa
      ? (i % 2 ? T.podAcc : mezcla(T.progA, T.progB, k))
      : caps.length ? mezcla(T.progA, T.progB, k) : T.podAcc;
    barras.push(
      <line key={i} x1={cx + r1 * Math.cos(a)} y1={cy + r1 * Math.sin(a)} x2={cx + r2 * Math.cos(a)} y2={cy + r2 * Math.sin(a)}
        stroke={color} strokeWidth={ancha ? 2.2 : 2.4} strokeLinecap="round" opacity={0.92} />
    );
  }

  const d = p.publicado ? new Date(p.publicado) : null;
  const dia = d ? d.toLocaleDateString(LOCALE(), { day: "numeric" }) : "";
  const mes = d ? d.toLocaleDateString(LOCALE(), { month: "short", year: "numeric" }).replace(".", "").toUpperCase() : "";
  const min = p.duracion ? `${Math.max(1, Math.round(p.duracion / 60))} MIN` : "";
  const gid = `yc-${String(p.id || p.slug).replace(/[^a-z0-9]/gi, "")}-${forma}`;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className={className} aria-hidden="true" focusable="false"
      style={{ display: "block", width: "100%", height: "auto", borderRadius: ancha ? 18 : 16, ...style }}>
      <defs>
        <radialGradient id={gid} cx={cx / W} cy={cy / H} r="0.9">
          <stop offset="0" stopColor="#5a3826" />
          <stop offset="0.55" stopColor={T.podBg} />
          <stop offset="1" stopColor="#2a190f" />
        </radialGradient>
      </defs>
      <rect width={W} height={H} fill={`url(#${gid})`} />
      <circle cx={cx} cy={cy} r={r0 + largo + 8} fill="none" stroke={T.podInk} strokeOpacity=".06" />
      {barras}
      <circle cx={cx} cy={cy} r={r0 - 4} fill={T.podInk} fillOpacity=".06" />
      <text x={cx} y={cy + 9} textAnchor="middle" fill={T.podAcc}
        style={{ font: `800 26px ${T.font}`, letterSpacing: "-.04em" }}>Y</text>

      <text x="16" y="26" fill={T.podAcc} style={{ font: `600 ${ancha ? 12 : 9.5}px ${T.mono}`, letterSpacing: ".08em" }}>
        {programaDe(p).toUpperCase()}
      </text>
      {p.idioma && p.idioma !== IDIOMA && (
        <text x="16" y={ancha ? 42 : 40} fill={T.podSec} style={{ font: `500 ${ancha ? 10.5 : 9}px ${T.mono}`, letterSpacing: ".08em" }}>
          {p.idioma === "en" ? "IN ENGLISH" : "IN SPANISH"}
        </text>
      )}
      <text x="14" y={ancha ? H - 34 : 96} fill={T.podInk} style={{ font: `800 ${ancha ? 62 : 52}px ${T.font}`, letterSpacing: "-.05em" }}>{dia}</text>
      <text x="16" y={ancha ? H - 16 : 114} fill={T.podSec} style={{ font: `600 ${ancha ? 11.5 : 9.5}px ${T.mono}`, letterSpacing: ".08em" }}>
        {ancha ? [mes, min].filter(Boolean).join(" · ") : mes}
      </text>
      {!ancha && <text x="16" y="128" fill={T.podSec} style={{ font: `600 9.5px ${T.mono}`, letterSpacing: ".08em" }}>{min}</text>}
    </svg>
  );
}
